import BlockOutlinedIcon from "@mui/icons-material/BlockOutlined";
import { useAlertContext, useConfirmationDialogContext } from "@shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { PortalAccountStateDto, SecondFactorStateDto } from "@/api";
import { useTranslation } from "@/translations";

import { PortalAccountController, UsePortalAccountParams } from "../portalAccess.types";

// Placeholder mutationFn for capabilities the subject does not offer: the hooks must be created
// unconditionally, but the corresponding handlers are undefined, so this can never actually run.
const rejectUnsupported: () => Promise<void> = () => Promise.reject(new Error("Action not supported for this subject"));

/**
 * Manages a subject's portal access (Kund:innen-Zugang / Zugang Ehrenamtliche): reads the current
 * state and exposes every access action as a ready-to-bind click handler. Actions that take access
 * away ask for confirmation first; sending the initial invitation grants access and runs directly.
 *
 * Every mutation opts out of the global error toast (inside the adapter): each `onError` already
 * shows a German message naming the failed action, and without the opt-out the middleware adds a
 * second toast carrying the raw English backend detail and its trace id.
 */
export function usePortalAccount({
  api,
  statusQueryKey,
  extraInvalidationKeys = [],
  texts,
}: UsePortalAccountParams): PortalAccountController {
  const { t } = useTranslation();
  const { showSuccess, showError } = useAlertContext();
  const { showConfirmationDialog } = useConfirmationDialogContext();
  const queryClient = useQueryClient();

  const {
    data: status,
    isPending,
    isError,
  } = useQuery({
    queryKey: statusQueryKey,
    queryFn: api.getStatus,
  });

  const invalidateAccountQueries = () => {
    void queryClient.invalidateQueries({ queryKey: statusQueryKey });
    for (const key of extraInvalidationKeys) {
      void queryClient.invalidateQueries({ queryKey: key });
    }
  };

  // A rejected action usually means the page is showing a state the server has already moved past
  // (e.g. the subject completed the registration in the portal meanwhile), so refresh on failure
  // too and the card corrects itself instead of only showing an error.
  const handleActionError = (message: string) => {
    invalidateAccountQueries();
    showError(message);
  };

  const outcome = (successMessage: string, errorMessage: string) => ({
    onSuccess: () => {
      invalidateAccountQueries();
      showSuccess(successMessage);
    },
    onError: () => handleActionError(errorMessage),
  });

  const sendInvitation = useMutation({
    mutationFn: api.sendInvitation,
    ...outcome(t("portalAccess.invitation.sendSuccess"), t("portalAccess.invitation.sendError")),
  });

  const resendInvitation = useMutation({
    mutationFn: api.resendInvitation,
    ...outcome(t("portalAccess.pending.resendSuccess"), t("portalAccess.pending.resendError")),
  });

  const withdrawInvitation = useMutation({
    mutationFn: api.withdrawInvitation ?? rejectUnsupported,
    ...outcome(t("portalAccess.pending.withdrawSuccess"), t("portalAccess.pending.withdrawError")),
  });

  const lock = useMutation({
    mutationFn: api.lock,
    ...outcome(t("portalAccess.manage.lockSuccess"), t("portalAccess.manage.lockError")),
  });

  const unlock = useMutation({
    mutationFn: api.unlock,
    ...outcome(t("portalAccess.manage.unlockSuccess"), t("portalAccess.manage.unlockError")),
  });

  const sendPasswordReset = useMutation({
    mutationFn: api.sendPasswordReset,
    ...outcome(t("portalAccess.manage.resetSuccess"), t("portalAccess.manage.resetError")),
  });

  const removePortalUser = useMutation({
    mutationFn: api.removeUser ?? rejectUnsupported,
    ...outcome(t("portalAccess.manage.removeUserSuccess"), t("portalAccess.manage.removeUserError")),
  });

  const onWithdraw = () => {
    showConfirmationDialog({
      dialogTitleText: t("portalAccess.pending.confirmWithdrawTitle"),
      dialogText: t("portalAccess.pending.confirmWithdrawText"),
      discardText: t("common.cancel"),
      agreeText: t("portalAccess.pending.confirmWithdrawConfirm"),
      agreeColor: "error",
      onClose: (confirmed) => {
        if (confirmed) {
          withdrawInvitation.mutate();
        }
      },
    });
  };

  const onLock = () => {
    showConfirmationDialog({
      dialogTitleText: t("portalAccess.manage.confirmLockTitle"),
      dialogText: texts.confirmLockText,
      discardText: t("common.cancel"),
      agreeText: t("portalAccess.manage.confirmLockConfirm"),
      agreeColor: "error",
      agreeIcon: <BlockOutlinedIcon />,
      onClose: (confirmed) => {
        if (confirmed) {
          lock.mutate();
        }
      },
    });
  };

  const onUnlock = () => {
    showConfirmationDialog({
      dialogTitleText: t("portalAccess.manage.confirmUnlockTitle"),
      dialogText: texts.confirmUnlockText,
      discardText: t("common.cancel"),
      agreeText: t("portalAccess.manage.confirmUnlockConfirm"),
      onClose: (confirmed) => {
        if (confirmed) {
          unlock.mutate();
        }
      },
    });
  };

  const onSendPasswordReset = () => {
    // Completing the reset also clears the second factor, so staff must be told before they
    // trigger the mail — but only when there is one to clear.
    const warnsAboutSecondFactor = status?.secondFactor === SecondFactorStateDto.Active;

    showConfirmationDialog({
      dialogTitleText: t("portalAccess.manage.confirmResetTitle"),
      // Two complete sentences composed as siblings rather than concatenated: the subject-specific
      // one and the shared second-factor warning each stay a whole translatable unit, and the
      // paragraph break between them is layout (the dialog renders with white-space: pre-wrap),
      // not a fragment either translator has to account for.
      dialogText: (
        <>
          {texts.confirmResetText(status?.email ?? "")}
          {warnsAboutSecondFactor && (
            <>
              {"\n\n"}
              {t("portalAccess.manage.confirmResetSecondFactor")}
            </>
          )}
        </>
      ),
      discardText: t("common.cancel"),
      agreeText: t("portalAccess.manage.confirmResetConfirm"),
      onClose: (confirmed) => {
        if (confirmed) {
          sendPasswordReset.mutate();
        }
      },
    });
  };

  const onRemoveUser = () => {
    showConfirmationDialog({
      dialogTitleText: t("portalAccess.manage.confirmRemoveUserTitle"),
      dialogText: t("portalAccess.manage.confirmRemoveUserText"),
      discardText: t("common.cancel"),
      agreeText: t("portalAccess.manage.confirmRemoveUserConfirm"),
      agreeColor: "error",
      onClose: (confirmed) => {
        if (confirmed) {
          removePortalUser.mutate();
        }
      },
    });
  };

  const isBusy =
    sendInvitation.isPending ||
    resendInvitation.isPending ||
    withdrawInvitation.isPending ||
    lock.isPending ||
    unlock.isPending ||
    sendPasswordReset.isPending ||
    removePortalUser.isPending;

  return {
    status,
    isPending,
    isError,
    isLocked: status?.state === PortalAccountStateDto.Locked,
    isBusy,
    onSendInvitation: sendInvitation.mutate,
    onResend: resendInvitation.mutate,
    onWithdraw: api.withdrawInvitation ? onWithdraw : undefined,
    onLock,
    onUnlock,
    onSendPasswordReset,
    onRemoveUser: api.removeUser ? onRemoveUser : undefined,
  };
}
